import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { CoachOfferStep1View } from "@/modules/coachOffers/components/coachOfferStep1View"
import {
  coachOfferFormDefaultValues,
  CoachOfferValidationSchema,
} from "@/modules/coachOffers/types/coachOfferFormTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { CoachOfferFormLayout } from "@modules/coachOffers/components/coachOfferFormLayout"
import { CoachOfferResumeView } from "@modules/coachOffers/components/coachOfferResumeView"
import { CoachOfferStep2View } from "@modules/coachOffers/components/coachOfferStep2View"
import { CoachOfferStep3View } from "@modules/coachOffers/components/coachOfferStep3View"
import { CoachOfferStep4View } from "@modules/coachOffers/components/coachOfferStep4View"
import type { CoachOfferEntity } from "@modules/coachOffers/entities/coachOfferEntity"
import { type CoachOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import {
  useCreateCoachOffer,
  useCreatePaymentOffer,
  useUpdateCoachOffer,
} from "@modules/coachOffers/hooks/useCoachOffers"
import { word } from "@modules/core/utils/i18n"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { useUser } from "@modules/users/hooks/useUser"
import { useStore } from "@tanstack/react-form"
import dayjs from "dayjs"
import { useEffect, useState } from "react"

type CoachOfferCreationFormProps = {
  initialOffer?: CoachOfferEntity
}

export function CoachOfferCreationForm({ initialOffer }: CoachOfferCreationFormProps) {
  const [step, setStep] = useState(initialOffer ? 2 : 1)

  const { setCurrentStackOnHide, sidebarNavigateBack } = useSidebar()
  const confirmPopup = useConfirmPopup()
  const user = useUser()
  const language = useLanguage()
  const createMutation = useCreateCoachOffer()
  const updateMutation = useUpdateCoachOffer(initialOffer?.id ?? "")
  const createPaymentOfferMutation = useCreatePaymentOffer(initialOffer?.id ?? "")

  const currency = user.stripeAccountCurrency ?? "EUR"

  const form = useAppForm({
    defaultValues: initialOffer ? initialOffer.fromEntityToFormValues() : coachOfferFormDefaultValues,
    validators: {
      onSubmit: CoachOfferValidationSchema(language),
    },
    onSubmit: async ({ value }) => {
      const paymentOffersInput = value.paymentOffers.map((po) => ({
        price: po.price !== null ? { value: po.price, currency } : null,
        recurringPeriod: po.recurringPeriod,
        type: po.type,
        title: po.title,
        count: po.count,
        minimumCommitmentPeriod:
          po.minimumCommitmentPeriod && !isNaN(Number(po.minimumCommitmentPeriod))
            ? dayjs.duration({ months: Number(po.minimumCommitmentPeriod) }).toISOString()
            : null,
        commitmentPeriodAutoRenewal: po.commitmentPeriodAutoRenewal,
        validityPeriodMonths: po.validityPeriodMonths,
        displayOrder: po.displayOrder,
        renewable: po.renewable,
      }))

      if (initialOffer) {
        await updateMutation.mutateAsync({
          name: value.name,
          description: value.description || null,
          showcaseDisplay: value.showcaseDisplay,
          priceDisplay: value.priceDisplay,
          autonomousPurchase: value.autonomousPurchase,
          displayOrder: null,
          imageKey: value.image?.key ?? null,
          rules: value.rules,
          programRestrictions: value.categoryIds,
          defaultHideSessionContent: value.defaultHideSessionContent,
          scoreboardVisibility: null,
        })

        const newPaymentOffers = value.paymentOffers.filter((po) => !po.id)
        await Promise.all(
          newPaymentOffers.map((po) =>
            createPaymentOfferMutation.mutateAsync({
              price: po.price !== null ? { value: po.price, currency } : null,
              recurringPeriod: po.recurringPeriod,
              type: po.type,
              title: po.title,
              count: po.count,
              minimumCommitmentPeriod:
                po.minimumCommitmentPeriod && !isNaN(Number(po.minimumCommitmentPeriod))
                  ? dayjs.duration({ months: Number(po.minimumCommitmentPeriod) }).toISOString()
                  : null,
              commitmentPeriodAutoRenewal: po.commitmentPeriodAutoRenewal,
              validityPeriodMonths: po.validityPeriodMonths,
              displayOrder: po.displayOrder,
              renewable: po.renewable,
            }),
          ),
        )
      } else {
        if (!value.type) return
        await createMutation.mutateAsync({
          name: value.name,
          type: value.type,
          description: value.description || null,
          showcaseDisplay: value.showcaseDisplay,
          priceDisplay: value.priceDisplay,
          autonomousPurchase: value.autonomousPurchase,
          displayOrder: null,
          paymentOffers: paymentOffersInput,
          imageKey: value.image?.key ?? null,
          rules: value.rules,
          programRestrictions: value.categoryIds,
          defaultHideSessionContent: value.defaultHideSessionContent,
          scoreboardVisibility: null,
        })
      }

      sidebarNavigateBack(false)
    },
  })

  const offerType = useStore(form.baseStore, (state) => state.values.type)

  const isDirty = useStore(
    form.baseStore,
    (state) => JSON.stringify(state.values) !== JSON.stringify(coachOfferFormDefaultValues),
  )

  useEffect(() => {
    setCurrentStackOnHide(async () => {
      if (isDirty) {
        return new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: word("coach.offers.form.closeMessage.title"),
            message: word("coach.offers.form.closeMessage.message"),
            accept: () => {
              resolve(true)
            },
            cancel: () => {
              resolve(false)
            },
          })
        })
      }
      return true
    })
  }, [isDirty])

  function handleTypeSelect(type: CoachOfferType) {
    form.setFieldValue("type", type)
    setStep(2)
  }

  function handleCancel() {
    setStep(1)
    form.reset()
  }

  async function handleNext() {
    if (step === 2) {
      const [nameErrors, descErrors, imageErrors] = await Promise.all([
        form.validateField("name", "change"),
        form.validateField("description", "change"),
        form.validateField("image", "change"),
      ])
      if (nameErrors.length > 0 || descErrors.length > 0 || imageErrors.length > 0) return
    }
    if (step === 3) {
      const [rulesErrors, categoryErrors, defaultHideSessionContentErrors] = await Promise.all([
        form.validateField("rules", "change"),
        form.validateField("categoryIds", "change"),
        form.validateField("defaultHideSessionContent", "change"),
      ])
      if (rulesErrors.length > 0 || categoryErrors.length > 0 || defaultHideSessionContentErrors.length > 0) {
        return
      }
    }
    if (step === 4) {
      const paymentOffersErrors = await form.validateField("paymentOffers", "change")
      if (paymentOffersErrors.length > 0) return
    }
    setStep((prev) => prev + 1)
  }

  function handlePrevious() {
    if (step === 2) {
      if (initialOffer) {
        sidebarNavigateBack()
      } else {
        handleCancel()
      }
    } else setStep((prev) => prev - 1)
  }

  if (step === 1) {
    return <CoachOfferStep1View onSelectType={handleTypeSelect} />
  }

  if (offerType === null) {
    return null
  }

  const isLoading = createMutation.isPending || updateMutation.isPending || createPaymentOfferMutation.isPending

  if (step === 5) {
    return (
      <CoachOfferResumeView
        formValues={form.state.values}
        onPrevious={() => {
          setStep(4)
        }}
        onSave={async () => {
          await form.handleSubmit()
        }}
        loading={isLoading}
      />
    )
  }

  function renderStep() {
    switch (step) {
      case 2:
        return <CoachOfferStep2View form={form} />
      case 3:
        return <CoachOfferStep3View form={form} />
      case 4:
        return <CoachOfferStep4View form={form} />
      default:
        return null
    }
  }

  return (
    <CoachOfferFormLayout
      type={offerType}
      step={step}
      onSecondaryAction={handlePrevious}
      onPrimaryAction={handleNext}
      isModifyingOffer={!!initialOffer}
    >
      {renderStep()}
    </CoachOfferFormLayout>
  )
}
